import {computed, onBeforeUnmount, unref, type ComponentPublicInstance, type MaybeRef} from 'vue'
import {requirePointerDragDrop} from '@toolify/client/src/modules/ui/composables/usePointerDragDrop'
import {toHtmlElement} from '@toolify/client/src/modules/ui/functions/toHtmlElement'

export function useDropList<T>(options: {
  orientation?: 'vertical' | 'horizontal'
  itemIds: MaybeRef<string[]>
  accepts: (payload: T) => boolean
  canDropOnItem?: (payload: T, itemId: string) => boolean
  onInsert: (payload: T, index: number) => void
  onDropOnItem?: (payload: T, itemId: string) => void
}) {
  const context = requirePointerDragDrop()
  const id = Symbol('drop-list')
  let unregister: (() => void) | null = null

  function bindElement(element: Element | ComponentPublicInstance | null) {
    unregister?.()
    unregister = null
    const htmlElement = toHtmlElement(element)
    if(!htmlElement) {
      return
    }
    unregister = context.registerDrop({
      id,
      kind: 'list',
      element: htmlElement,
      orientation: options.orientation ?? 'vertical',
      itemIds: options.itemIds,
      accepts: payload => options.accepts(payload as T),
      canDropOnItem: options.canDropOnItem
        ? (payload, itemId) => options.canDropOnItem(payload as T, itemId)
        : undefined,
      onInsert: (payload, index) => options.onInsert(payload as T, index),
      onDropOnItem: options.onDropOnItem
        ? (payload, itemId) => options.onDropOnItem(payload as T, itemId)
        : undefined,
    })
  }

  onBeforeUnmount(() => {
    unregister?.()
  })

  const insertIndex = computed(() => context.hoveredDropId.value === id ? context.insertIndex.value : null)
  const insertEdge = computed(() => context.hoveredDropId.value === id ? context.insertEdge.value : null)
  const insertLineStyle = computed(() => context.hoveredDropId.value === id ? context.insertLineStyle.value : null)
  const hoveredItemId = computed(() => context.hoveredDropId.value === id ? context.hoveredItemId.value : null)

  function isItemDropHovered(itemId: MaybeRef<string>) {
    return computed(() => hoveredItemId.value === unref(itemId))
  }

  return {
    listRef: bindElement,
    insertIndex,
    insertEdge,
    insertLineStyle,
    hoveredItemId,
    isItemDropHovered,
  }
}
